import React from 'react';
import type { FaxAccountVo, TProviderFaxNumber } from 'types/eFax';
import { faxNumberStatus } from 'api/fax';
import { getUserId } from 'utils/localstore';

const useFaxNumber = (
    providerId?: number,
): [
    TProviderFaxNumber | undefined,
    boolean,
    () => void,
    { hasCancelledAccount: boolean; pendingPortFaxNumber?: string },
] => {
    const [loading, setLoading] = React.useState(false);
    const [items, setItems] = React.useState<TProviderFaxNumber>();
    const [hasCancelledAccount, setHasCancelledAccount] = React.useState(false);
    const [pendingPortFaxNumber, setPendingPortFaxNumber] = React.useState<string | undefined>();

    const mapActiveAccountToProviderFax = React.useCallback(
        (account?: FaxAccountVo): TProviderFaxNumber | undefined => {
            if (!account?.faxNumber) return undefined;
            const pid = providerId || getUserId()!;
            return {
                providerId: pid,
                faxNumber: account.faxNumber,
                faxNumberDisplay: account.faxNumberDisplay,
                source: 'schedule',
                status: account.status || 'ACTIVE',
                boundAt: new Date().toISOString(),
            };
        },
        [providerId],
    );

    const fetchData = async () => {
        if (loading) return;
        try {
            setLoading(true);
            const result = await faxNumberStatus();
            const envelope = result?.data as
                | {
                      data?: {
                          activeAccount?: FaxAccountVo;
                          hasCancelledAccount?: boolean;
                          pendingPortFaxNumber?: string;
                      };
                  }
                | undefined;
            const active = mapActiveAccountToProviderFax(envelope?.data?.activeAccount);
            setHasCancelledAccount(Boolean(envelope?.data?.hasCancelledAccount));
            setPendingPortFaxNumber(envelope?.data?.pendingPortFaxNumber || undefined);
            setItems(active);
        } catch (e) {
            console.error(e);
            setHasCancelledAccount(false);
            setPendingPortFaxNumber(undefined);
            setItems(undefined);
        }
        setLoading(false);
    };

    React.useEffect(() => {
        fetchData();
    }, [providerId]);

    return [items, loading, fetchData, { hasCancelledAccount, pendingPortFaxNumber }];
};

export default useFaxNumber;
